import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { ShoppingBag, User, LogOut, LayoutDashboard, Menu as MenuIcon, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ONLINE_ORDERING_ENABLED } from "@/lib/features";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const linkCls = ({ isActive }) =>
    `text-sm font-medium transition-colors ${isActive ? "text-[hsl(var(--primary))]" : "text-neutral-700 hover:text-[hsl(var(--primary))]"}`;

  return (
      <header className="sticky top-0 z-40 w-full backdrop-blur-xl bg-white/85 border-b border-neutral-200/60">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-24 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          <img
            src="https://customer-assets-0z36b82j.emergentagent.net/job_sandar-eats/artifacts/v9aauz8h_sandar-logo.png"
            alt="Sandar Sushi"
            className="h-16 w-auto object-contain"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/menu" className={linkCls} data-testid="nav-menu">Menu</NavLink>
          <NavLink to="/locations" className={linkCls} data-testid="nav-locations">Locations</NavLink>
          <NavLink to="/home" className={linkCls} data-testid="nav-home">Home</NavLink>
          <NavLink to="/careers" className={linkCls} data-testid="nav-careers">Careers</NavLink>
          <NavLink to="/story" className={linkCls} data-testid="nav-story">Our Story</NavLink>
          <NavLink to="/markets" className={linkCls} data-testid="nav-markets">Markets</NavLink>
        </nav>

        <div className="flex items-center gap-2">
          {ONLINE_ORDERING_ENABLED && (
            <Link to="/cart" className="relative p-2 rounded-full hover:bg-neutral-100 transition-colors" data-testid="nav-cart">
              <ShoppingBag className="w-5 h-5" />
              {count > 0 && (
                <span data-testid="cart-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[hsl(var(--primary))] text-white text-[10px] font-bold flex items-center justify-center">{count}</span>
              )}
            </Link>
          )}

          {user && user !== false ? (
            <div className="hidden sm:flex items-center gap-2">
              {(user.role === "admin" || user.role === "employee") && (
                <Button variant="ghost" size="sm" onClick={() => nav("/admin")} data-testid="nav-admin">
                  <LayoutDashboard className="w-4 h-4 mr-1" /> Admin
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => nav("/account")} data-testid="nav-account">
                <User className="w-4 h-4 mr-1" /> {user.name?.split(" ")[0]}
              </Button>
              <Button variant="ghost" size="sm" onClick={logout} data-testid="nav-logout">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => nav("/login")} className="hidden sm:inline-flex" data-testid="nav-login">Login</Button>
              <Button size="sm" onClick={() => nav("/register")} className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="nav-register">Sign up</Button>
            </>
          )}

          <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X className="w-5 h-5"/> : <MenuIcon className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-neutral-200 bg-white">
          <div className="px-6 py-4 flex flex-col gap-3">
            <NavLink
              to="/menu"
              onClick={() => setOpen(false)}
              className={linkCls}
              data-testid="mobile-nav-menu"
            >
              Menu
            </NavLink>
            <NavLink
              to="/locations"
              onClick={() => setOpen(false)}
              className={linkCls}
              data-testid="mobile-nav-locations"
            >
              Locations
            </NavLink>
            <NavLink
              to="/home"
              onClick={() => setOpen(false)}
              className={linkCls}
              data-testid="mobile-nav-home"
            >
              Home
            </NavLink>
            <NavLink
              to="/careers"
              onClick={() => setOpen(false)}
              className={linkCls}
              data-testid="mobile-nav-careers"
            >
              Careers
            </NavLink>
            <NavLink
              to="/story"
              onClick={() => setOpen(false)}
              className={linkCls}
              data-testid="mobile-nav-story"
            >
              Our Story
            </NavLink>
            <NavLink
              to="/markets"
              onClick={() => setOpen(false)}
              className={linkCls}
              data-testid="mobile-nav-markets"
            >
              Markets
            </NavLink>
            {user && user !== false ? (
              <>
                <NavLink to="/account" onClick={() => setOpen(false)} className={linkCls}>My Account</NavLink>
                {(user.role === "admin" || user.role === "employee") && <NavLink to="/admin" onClick={() => setOpen(false)} className={linkCls}>Admin</NavLink>}
                <button onClick={() => { logout(); setOpen(false); }} className="text-left text-sm text-red-600">Logout</button>
              </>
            ) : (
              <>
                <NavLink to="/login" onClick={() => setOpen(false)} className={linkCls}>Login</NavLink>
                <NavLink to="/register" onClick={() => setOpen(false)} className={linkCls}>Sign up</NavLink>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
